/**
 * Server-Sent Events を POST + Authorization ヘッダ付きで受信する簡易クライアント。
 * ブラウザ標準の EventSource は GET のみ・カスタムヘッダ不可のため、fetch + ReadableStream で自前実装する。
 */
import { API_BASE, handleUnauthorized } from './api';

export type SSEHandlers = {
  onEvent: (event: string, data: string) => void;
  onError?: (err: Error) => void;
};

export const postSSE = async (
  path: string,
  body: unknown,
  handlers: SSEHandlers,
  signal?: AbortSignal,
): Promise<void> => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });
  handleUnauthorized(res.status);

  if (!res.ok || !res.body) {
    const errBody = await res.json().catch(() => null);
    throw new Error(errBody?.error?.message ?? `リクエストに失敗しました (status=${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emitBlock = (block: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length > 0) handlers.onEvent(event, dataLines.join('\n'));
  };

  // 本番事故 (2026-08-04) 対応: サーバー側の異常 (§8-XX相当のstall) やネットワーク瞬断で
  // 接続が静かに死んだ場合、reader.read() が無期限にpendingのままになり「生成中」表示が
  // 永久に消えない事故があった。1回のreadあたりの無応答許容時間を設け、超過時は明示的に
  // エラーとして扱う (サーバー側のANTHROPIC_CALL_STALL_MS=180sより長く取り、サーバーが自力で
  // フォールバック保存してSSEを閉じる猶予を優先する)。
  const READ_IDLE_TIMEOUT_MS = 220_000;
  const readWithIdleTimeout = async (): Promise<ReadableStreamReadResult<Uint8Array>> => {
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(() => reject(new Error('SSE_IDLE_TIMEOUT')), READ_IDLE_TIMEOUT_MS);
    });
    try {
      return await Promise.race([reader.read(), timeoutPromise]);
    } finally {
      clearTimeout(timeoutHandle!);
    }
  };

  while (true) {
    let result: ReadableStreamReadResult<Uint8Array>;
    try {
      result = await readWithIdleTimeout();
    } catch (err) {
      if (err instanceof Error && err.message === 'SSE_IDLE_TIMEOUT') {
        reader.cancel().catch(() => { /* 既に切断済みの可能性があるため失敗は無視 */ });
        throw new Error('サーバーからの応答が一定時間ありませんでした。時間をおいて再度お試しください');
      }
      throw err;
    }
    if (result.done) break;
    buffer += decoder.decode(result.value, { stream: true });

    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';
    for (const block of blocks) emitBlock(block);
  }

  // テスト網羅性レビュー CRITICAL 反映 (2026-08-02): ストリーム終了時、末尾が \n\n で終端されずに
  // バッファへ残っていたデータ (ネットワーク瞬断や接続クローズのタイミングで起こりうる) を無条件破棄
  // せず、最終フラッシュとして処理する。これにより「done/error イベントだけが静かに消える」事故を防ぐ。
  buffer += decoder.decode();
  if (buffer.trim()) emitBlock(buffer);
};
